import React from "react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Tooltip from "@mui/material/Tooltip";

/**
 * Violet info banner shown in card editors when AI generation produced
 * a `metadata.media_suggestion` but the card has no real media yet.
 * Sits above the upload control as guidance to the author.
 *
 * Purely presentational. The "Generate image" button just invokes
 * `onGenerate` — the parent modal owns the request and attaches the
 * result through its _clientId-keyed queue, so a generation started on
 * card 3 lands on card 3 even if the author selects another card while
 * the render runs (this component's instance is REUSED across card
 * selection because RouteCardEditor renders editors unkeyed — it must
 * never capture "the current card" for an async write itself).
 *
 * Without `onGenerate` (poll/trivia editors — the player doesn't render
 * media on those card types yet) the button renders disabled.
 * `generating` should be true while the parent's queue has this card's
 * image in flight; it both shows progress and prevents a duplicate
 * paid render of the same suggestion.
 */
export function MediaSuggestionBanner({ card, onGenerate, generating }) {
  const suggestion = card?.metadata?.media_suggestion || null;
  const hasMedia = Boolean(card?.media_url || card?.media_file_uuid);
  if (!suggestion || hasMedia) return null;

  const canGenerate = card?.card_type === "image_text" && Boolean(onGenerate);

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 mb-3">
      <div className="flex items-start gap-2">
        <AutoAwesomeIcon
          className="text-violet-600 mt-0.5 shrink-0"
          style={{ fontSize: 16 }}
        />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-violet-700 uppercase tracking-wide mb-0.5">
            Suggested image
          </p>
          <p className="text-sm text-slate-800 italic leading-snug">
            {suggestion}
          </p>
        </div>
      </div>
      <div className="mt-2 flex justify-end">
        {canGenerate ? (
          <button
            type="button"
            onClick={onGenerate}
            disabled={Boolean(generating)}
            className={[
              "inline-flex items-center gap-1 px-2.5 py-1 rounded-md border text-[11px] font-medium transition-colors",
              generating
                ? "bg-white border-violet-200 text-violet-400 cursor-wait"
                : "bg-violet-600 border-violet-600 text-white hover:bg-violet-700",
            ].join(" ")}
          >
            {generating ? (
              <>
                <span className="w-3 h-3 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <AutoAwesomeIcon style={{ fontSize: 12 }} />
                Generate image
              </>
            )}
          </button>
        ) : (
          <Tooltip title="AI images aren't available for this card type yet — upload an image below">
            <span>
              <button
                type="button"
                disabled
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white border border-violet-200 text-[11px] font-medium text-violet-400 cursor-not-allowed"
              >
                <AutoAwesomeIcon style={{ fontSize: 12 }} />
                Generate image
              </button>
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

export default MediaSuggestionBanner;
